import { useEffect, useState } from "react";
import { Phone, type LucideIcon } from "lucide-react";
import { trackDemoClick } from "@/lib/analytics";

interface TopBarProps {
  isVisible: boolean;
}

interface TopBarMessage {
  icon?: LucideIcon;
  text: string;
  href: string;
  aria: string;
  tracking: string;
}

// Evergreen demo announcement. The text includes the calendar emoji, so no separate icon is rendered.
const messages: readonly TopBarMessage[] = [
  {
    text: "🗓️ Book a 45-minute ComplyHub demo →",
    href: "/demo#book",
    aria: "Book a 45-minute ComplyHub demonstration",
    tracking: "top_bar_demo",
  },
];

const TopBar = ({ isVisible }: TopBarProps) => {
  const [index, setIndex] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const listener = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % messages.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  const active = messages[reduceMotion ? 0 : index];
  const Icon = active.icon;
  const isExternal = active.href.startsWith("http");
  const shouldAnimate = !reduceMotion && messages.length > 1;

  return (
    <div 
      data-fixed-topbar="true"
      role="region"
      aria-label="Announcements"
      className={`fixed left-0 right-0 z-[80] h-9 bg-ch-acai transition-all duration-300 ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      }`}
      style={{ top: "var(--banner-offset, 0px)" }}
    >
      <div className="container h-full flex items-center justify-between">
        <a 
          href="tel:1300729455" 
          className="flex items-center gap-1.5 text-white hover:text-white/90 transition-colors"
          aria-label="Call ComplyHub on 1300 729 455"
        >
          <Phone className="w-3.5 h-3.5" aria-hidden="true" />
          <span className="text-white text-xs sm:text-sm font-medium">1300 729 455</span>
        </a>

        {/* Rotating centre announcement */}
        <a
          key={active.text}
          href={active.href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="ann-msg flex items-center gap-1.5 text-white hover:text-white/90 transition-colors max-w-[70%] truncate"
          onClick={() => trackDemoClick(active.tracking)}
          aria-label={active.aria}
          style={shouldAnimate ? { animation: "annFade 6s ease-in-out both" } : undefined}
        >
          {Icon && <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
          <span className="text-white text-xs sm:text-sm font-semibold truncate">
            {active.text}
          </span>
        </a>

        {/* Contact link */}
        <a
          href="/contact"
          className="flex items-center justify-end w-[100px] text-white hover:text-white/90 transition-colors"
          aria-label="Contact ComplyHub"
        >
          <span className="text-white text-xs sm:text-sm font-semibold">Contact</span>
        </a>
      </div>
    </div>
  );
};

export default TopBar;
